import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { Checkbox } from 'app-ui/primitives/input';
import { themeCssVariables } from 'app-ui/theme';
import {
  type CollaboratorFieldsFragment,
  type SkillFieldsFragment,
  type TeamFieldsFragment,
} from '~/generated/graphql';
const StyledGroup = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: ${themeCssVariables.spacing[2]};
  margin: 0;
  padding: 0;
`;
const StyledLabel = styled.label`
  align-items: center;
  display: flex;
  gap: ${themeCssVariables.spacing[2]};
`;
type InterventionAssignmentPickerProps = {
  collaborators: CollaboratorFieldsFragment[];
  teams: TeamFieldsFragment[];
  skills: SkillFieldsFragment[];
  collaboratorIds: string[];
  skillIds: string[];
  onCollaboratorsChange: (collaboratorIds: string[]) => void;
  onSkillsChange: (skillIds: string[]) => void;
  disabled: boolean;
};
export const InterventionAssignmentPicker = ({
  collaborators,
  teams,
  skills,
  collaboratorIds,
  skillIds,
  onCollaboratorsChange,
  onSkillsChange,
  disabled,
}: InterventionAssignmentPickerProps) => {
  const { t } = useLingui();
  const groups = [
    ...teams.map((team) => ({ id: team.id, name: team.name })),
    { id: null, name: t`No team` },
  ];
  const handleToggleCollaborator = (id: string, checked: boolean) =>
    onCollaboratorsChange(
      checked
        ? [...collaboratorIds, id]
        : collaboratorIds.filter((collaboratorId) => collaboratorId !== id),
    );
  const handleToggleSkill = (id: string, checked: boolean) =>
    onSkillsChange(
      checked
        ? [...skillIds, id]
        : skillIds.filter((skillId) => skillId !== id),
    );
  return (
    <>
      <StyledGroup>
        <legend>{t`Assigned collaborators`}</legend>
        {groups.map((group) => {
          const members = collaborators.filter(
            (collaborator) => collaborator.teamId === group.id,
          );
          return (
            members.length > 0 && (
              <StyledGroup key={group.id ?? 'none'}>
                <legend>{group.name}</legend>
                {members.map((collaborator) => (
                  <StyledLabel key={collaborator.id}>
                    <Checkbox
                      checked={collaboratorIds.includes(collaborator.id)}
                      disabled={disabled}
                      onCheckedChange={(checked) =>
                        handleToggleCollaborator(collaborator.id, checked)
                      }
                    />
                    {collaborator.fullName}
                    {!collaborator.isActive && ` (${t`Inactive`})`}
                  </StyledLabel>
                ))}
              </StyledGroup>
            )
          );
        })}
        {collaborators.length === 0 && <p>{t`No available collaborators`}</p>}
      </StyledGroup>
      <StyledGroup>
        <legend>{t`Required skills`}</legend>
        {skills.map((skill) => (
          <StyledLabel key={skill.id}>
            <Checkbox
              checked={skillIds.includes(skill.id)}
              disabled={disabled}
              onCheckedChange={(checked) =>
                handleToggleSkill(skill.id, checked)
              }
            />
            {skill.name}
          </StyledLabel>
        ))}
        {skills.length === 0 && <p>{t`No skills yet`}</p>}
      </StyledGroup>
    </>
  );
};
